import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Card, CardContent, CardHeader, Chip, Grid, Stack, Typography, useMediaQuery, useTheme, } from "@mui/material";
import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrieveUserLeagueMatchPermissions, useRetrieveUserLeagueMatchStatsPermissions, } from "../api/PermissionApiProvider";
import { useDeleteLeagueMatch, useRetrieveLeagueMatch, } from "../api/SeasonApiProvider";
import { useRetrievePlayersByTeamId } from "../ApiProvider";
import { UserContext } from "../auth/UserProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { NotFound } from "../nav/NotFound";
import { findPlayerById } from "../players/utils";
import { findPlayerLeagueMatchStatByPlayerAndTeam } from "../stats/utils";
import { SquadCardWithStats } from "../teams/SquadCardWithStats";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { ResponsiveActionMenu, } from "../utils/ResponsiveActionMenu";
import { useNumericParams } from "../utils/useNumericParams";
import { MatchDateTimeHeader } from "./MatchDateTimeHeader";
import { MatchScoreboard } from "./MatchScoreboard";
const LeagueMatch = () => {
    const { id } = useNumericParams();
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showDelete, setShowDelete] = useState(false);
    const { data: permissions, isLoading: permissionsIsLoading, error: permissionsError, } = useRetrieveUserLeagueMatchPermissions({ leagueMatchId: id });
    const { canView = false, canModify = false } = permissions || {};
    const { data: statsPermissions, isLoading: statsPermissionsIsLoading, error: statsPermissionsError, } = useRetrieveUserLeagueMatchStatsPermissions({
        leagueMatchId: id,
        enabled: !!user,
    });
    const { canModifyTeams = [] } = statsPermissions || {};
    const canModifyStats = canModifyTeams.length > 0;
    const { isLoading: leagueMatchIsLoading, error: leagueMatchError, data: leagueMatch, } = useRetrieveLeagueMatch({ id, enabled: canView });
    const deleteMutation = useDeleteLeagueMatch({
        onSuccess: () => {
            navigate(`/leagues/${leagueMatch?.leagueId}/seasons/${leagueMatch?.seasonId}`);
        },
    });
    const homeTeam = leagueMatch?.homeTeam;
    const awayTeam = leagueMatch?.awayTeam;
    const homeTeamId = homeTeam?.id;
    const awayTeamId = awayTeam?.id;
    const { data: homeTeamPlayers, error: homeTeamPlayersError, isLoading: homeTeamPlayersIsLoading, } = useRetrievePlayersByTeamId({ teamId: homeTeamId, enabled: !!user });
    const { data: awayTeamPlayers, error: awayTeamPlayersError, isLoading: awayTeamPlayersIsLoading, } = useRetrievePlayersByTeamId({ teamId: awayTeamId, enabled: !!user });
    if (permissionsIsLoading || (user && statsPermissionsIsLoading)) {
        return _jsx(Loading, {});
    }
    if (permissionsError || (user && statsPermissionsError)) {
        return _jsx(ErrorView, { error: permissionsError || statsPermissionsError });
    }
    if (!canView) {
        return _jsx(NotFound, {});
    }
    if (leagueMatchIsLoading ||
        (user && homeTeamId && homeTeamPlayersIsLoading) ||
        (user && awayTeamId && awayTeamPlayersIsLoading)) {
        return _jsx(Loading, {});
    }
    if (id === undefined) {
        return _jsx(NotFound, {});
    }
    if (leagueMatchError ||
        (user && homeTeamPlayersError) ||
        (user && awayTeamPlayersError) ||
        !leagueMatch) {
        return _jsx(ErrorView, {});
    }
    const getHomePlayer = findPlayerById(homeTeamPlayers || []);
    const getAwayPlayer = findPlayerById(awayTeamPlayers || []);
    const getPlayerLeagueMatchStat = findPlayerLeagueMatchStatByPlayerAndTeam(leagueMatch?.playerLeagueMatchStats || []);
    // Get players who played in the match
    const homePlayers = (leagueMatch.playerLeagueMatchStats || [])
        .filter((stat) => stat.teamId === homeTeamId && stat.played !== false)
        .map((stat) => getHomePlayer(stat.playerId))
        .filter((player) => player !== undefined);
    const awayPlayers = (leagueMatch.playerLeagueMatchStats || [])
        .filter((stat) => stat.teamId === awayTeamId && stat.played !== false)
        .map((stat) => getAwayPlayer(stat.playerId))
        .filter((player) => player !== undefined);
    // Format scorers
    const formatMatchScorer = (playerId, teamId, lastName) => {
        const stats = getPlayerLeagueMatchStat(playerId, teamId);
        if (!stats?.goalsScored)
            return undefined;
        const goalsScored = stats.goalsScored === 1 ? "" : ` (${stats.goalsScored})`;
        return `${lastName}${goalsScored}`;
    };
    const homeScorers = homePlayers
        .map((player) => formatMatchScorer(player.id, homeTeamId, player?.externalUser?.lastName ?? player?.user?.lastName))
        .filter((scorer) => scorer !== undefined);
    const awayScorers = awayPlayers
        .map((player) => formatMatchScorer(player.id, awayTeamId, player?.externalUser?.lastName ?? player?.user?.lastName))
        .filter((scorer) => scorer !== undefined);
    const sentOffFor = (players, teamId) => players
        .filter((player) => (getPlayerLeagueMatchStat(player.id, teamId)?.redCards ?? 0) > 0)
        .map((player) => player?.externalUser?.lastName ?? player?.user?.lastName)
        .filter((name) => name !== undefined && name !== null);
    const homeSentOff = sentOffFor(homePlayers, homeTeamId);
    const awaySentOff = sentOffFor(awayPlayers, awayTeamId);
    const result = leagueMatch.leagueMatchResult;
    const hasResult = result !== null && result !== undefined;
    const getStatusColor = (status) => {
        switch (status) {
            case "completed":
                return "success";
            case "in_progress":
                return "warning";
            case "postponed":
            case "cancelled":
                return "error";
            default:
                return "default";
        }
    };
    const getStatusLabel = (status) => {
        switch (status) {
            case "scheduled":
                return "Scheduled";
            case "in_progress":
                return "In Progress";
            case "completed":
                return "Full Time";
            case "postponed":
                return "Postponed";
            case "cancelled":
                return "Cancelled";
            default:
                return status;
        }
    };
    const actions = [
        ...(canModify
            ? [
                {
                    icon: _jsx(EditIcon, {}),
                    label: "Edit Match",
                    to: `/leagues/${leagueMatch.leagueId}/seasons/${leagueMatch.seasonId}/matches/${id}/update`,
                },
            ]
            : []),
        ...(canModifyStats
            ? [
                {
                    icon: _jsx(BarChartIcon, {}),
                    label: "Edit Stats",
                    to: `/leagues/${leagueMatch.leagueId}/seasons/${leagueMatch.seasonId}/matches/${id}/stats/update`,
                },
            ]
            : []),
        ...(canModify
            ? [
                {
                    icon: _jsx(DeleteIcon, {}),
                    label: "Delete",
                    onClick: () => setShowDelete(true),
                },
            ]
            : []),
    ];
    const infoItem = (text, to) => to && user ? (_jsx(Typography, { component: Link, to: to, variant: "body2", color: "primary.main", sx: {
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
        }, children: text })) : (_jsx(Typography, { variant: "body2", color: "text.secondary", children: text }));
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: deleteMutation.isError, message: "Failed to delete match. Please try again!" }), _jsx(DeleteDialog, { open: showDelete, title: "Delete League Match?", onCancel: () => setShowDelete(false), onConfirm: () => deleteMutation.mutate(id) }), _jsxs(Card, { sx: { mb: 3 }, children: [_jsx(CardHeader, { title: _jsxs(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", children: [_jsx(Box, { sx: { flex: 1 } }), _jsx(MatchDateTimeHeader, { kickOff: leagueMatch.kickOff, venueName: leagueMatch.venue?.name, pitchName: leagueMatch.pitchName, venueLink: user && leagueMatch.venue
                                        ? `/venues/${leagueMatch.venue.id}`
                                        : undefined, subtitle: _jsxs(Stack, { direction: "row", spacing: 0.75, alignItems: "center", justifyContent: "center", flexWrap: "wrap", children: [infoItem(leagueMatch.leagueName, `/leagues/${leagueMatch.leagueId}`), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "\u00B7" }), infoItem(leagueMatch.seasonName, `/leagues/${leagueMatch.leagueId}/seasons/${leagueMatch.seasonId}`), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "\u00B7" }), infoItem(`Round ${leagueMatch.round}`)] }) }), _jsx(Box, { sx: { flex: 1, display: "flex", justifyContent: "flex-end" }, children: actions.length > 0 && (_jsx(ResponsiveActionMenu, { actions: actions, isMobile: isMobile })) })] }) }), _jsx(CardContent, { children: _jsx(MatchScoreboard, { team1: {
                                name: homeTeam.name,
                                link: user && homeTeamId ? `/teams/${homeTeamId}` : undefined,
                            }, team2: {
                                name: awayTeam.name,
                                link: user && awayTeamId ? `/teams/${awayTeamId}` : undefined,
                            }, score1: result?.homeTeamScore, score2: result?.awayTeamScore, scorers1: homeScorers, scorers2: awayScorers, sentOff1: homeSentOff, sentOff2: awaySentOff, topContent: _jsxs(Stack, { direction: "row", spacing: 1, justifyContent: "center", children: [_jsx(Chip, { label: getStatusLabel(leagueMatch.status), color: getStatusColor(leagueMatch.status), size: "small" }), leagueMatch.matchType !== "regular" && (_jsx(Chip, { label: leagueMatch.matchType === "friendly"
                                            ? "Friendly"
                                            : "Post-Season", size: "small", variant: "outlined", color: leagueMatch.matchType === "friendly" ? "info" : "warning" }))] }) }) })] }), hasResult && (homePlayers.length > 0 || awayPlayers.length > 0) && (_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, md: 6, children: _jsx(SquadCardWithStats, { team: homeTeam, players: homePlayers, getPlayerStat: (playerId) => getPlayerLeagueMatchStat(playerId, homeTeamId) }) }), _jsx(Grid, { item: true, xs: 12, md: 6, children: _jsx(SquadCardWithStats, { team: awayTeam, players: awayPlayers, getPlayerStat: (playerId) => getPlayerLeagueMatchStat(playerId, awayTeamId) }) })] }))] }));
};
export default LeagueMatch;
